import { isAuthenticated } from './api';
/**
 * WebSocket Service for Real-time Updates
 * Handles: Messages, Notifications, Task Updates, User Status
 */

class WebSocketService {
  constructor() {
    this.ws = null;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 3000;
    this.listeners = new Map();
    this.isConnected = false;
    this.pendingMessages = [];
    this.heartbeatInterval = null;
    this.rooms = new Set();
  }

  /**
   * Connect to WebSocket server
   */
  connect() {
    // Phase B (Feb 2026): no longer reads a token out of sessionStorage.
    // The HttpOnly auth cookie carries credentials on the WS handshake;
    // we just gate the connect call on the in-memory authenticated flag.
    if (!isAuthenticated()) {

      return;
    }

    // Get WebSocket URL from API URL
    const apiUrl = process.env.REACT_APP_BACKEND_URL || '';
    const wsProtocol = apiUrl.startsWith('https') ? 'wss' : 'ws';
    const wsHost = apiUrl.replace(/^https?:\/\//, '');
    // JWT is in HTTP-only cookie — browser sends it automatically with the WS handshake
    const wsUrl = `${wsProtocol}://${wsHost}/ws`;

    try {
      this.ws = new WebSocket(wsUrl);

      this.ws.onopen = () => {
        // WebSocket connected
        this.isConnected = true;
        this.reconnectAttempts = 0;

        // Send pending messages
        while (this.pendingMessages.length > 0) {
          const msg = this.pendingMessages.shift();
          this.send(msg);
        }

        // Rejoin rooms
        this.rooms.forEach(roomId => {
          this.send({ type: 'join_room', room_id: roomId });
        });

        // Start heartbeat
        this.startHeartbeat();

        // Emit connected event
        this.emit('connected', { timestamp: new Date().toISOString() });
      };

      this.ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          this.handleMessage(data);
        } catch (error) { console.warn('WS: malformed message', error.message); }
      };

      this.ws.onclose = (event) => {
        // WebSocket disconnected
        this.isConnected = false;
        this.stopHeartbeat();
        this.emit('disconnected', { code: event.code, reason: event.reason });

        // Attempt reconnection
        if (this.reconnectAttempts < this.maxReconnectAttempts) {
          this.reconnectAttempts++;
          // WebSocket reconnecting
          setTimeout(() => this.connect(), this.reconnectDelay);
        }
      };

      this.ws.onerror = (error) => {
        this.emit('error', { error });
      };

    } catch (error) { console.warn('WS: connection failed, auto-reconnect will retry', error.message); }
  }

  /**
   * Disconnect WebSocket
   */
  disconnect() {
    this.stopHeartbeat();
    if (this.ws) {
      this.ws.close(1000, 'Client disconnect');
      this.ws = null;
    }
    this.isConnected = false;
    this.rooms.clear();
  }

  /**
   * Send message through WebSocket
   */
  send(data) {
    if (this.isConnected && this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(data));
    } else {
      // Queue message for later
      this.pendingMessages.push(data);
    }
  }

  /**
   * Handle incoming WebSocket messages
   */
  handleMessage(data) {
    const { type } = data;

    // Emit to specific listeners
    this.emit(type, data);

    // Handle special message types
    switch (type) {
      case 'pong':
        // Heartbeat response
        break;

      case 'new_message':
        this.emit('message', data.message);
        break;

      case 'new_notification':
        this.emit('notification', data.notification);
        break;

      case 'task_update':
        this.emit('task', { action: data.action, task: data.task });
        break;

      case 'project_update':
        this.emit('project', { action: data.action, project: data.project });
        break;

      case 'user_status':
        this.emit('userStatus', { userId: data.user_id, status: data.status });
        break;

      case 'typing':
        this.emit('typing', { senderId: data.sender_id, conversationId: data.conversation_id });
        break;

      case 'stop_typing':
        this.emit('stopTyping', { senderId: data.sender_id });
        break;

      case 'dashboard_refresh':
        this.emit('dashboardRefresh', data);
        break;

      default:
        // Emit generic event
        this.emit('message_received', data);
    }
  }

  /**
   * Start heartbeat to keep connection alive
   */
  startHeartbeat() {
    this.heartbeatInterval = setInterval(() => {
      this.send({ type: 'ping' });
    }, 30000); // Every 30 seconds
  }

  /**
   * Stop heartbeat
   */
  stopHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  /**
   * Join a room (project, conversation, etc.)
   */
  joinRoom(roomId) {
    this.rooms.add(roomId);
    this.send({ type: 'join_room', room_id: roomId });
  }

  /**
   * Leave a room
   */
  leaveRoom(roomId) {
    this.rooms.delete(roomId);
    this.send({ type: 'leave_room', room_id: roomId });
  }

  /**
   * Send typing indicator
   */
  sendTyping(receiverId, conversationId) {
    this.send({
      type: 'typing',
      receiver_id: receiverId,
      conversation_id: conversationId
    });
  }

  /**
   * Send stop typing indicator
   */
  sendStopTyping(receiverId) {
    this.send({
      type: 'stop_typing',
      receiver_id: receiverId
    });
  }

  /**
   * Get online users
   */
  getOnlineUsers() {
    this.send({ type: 'get_online_users' });
  }

  /**
   * Subscribe to events
   */
  on(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event).add(callback);

    // Return unsubscribe function
    return () => {
      this.listeners.get(event)?.delete(callback);
    };
  }

  /**
   * Unsubscribe from events
   */
  off(event, callback) {
    if (callback) {
      this.listeners.get(event)?.delete(callback);
    } else {
      this.listeners.delete(event);
    }
  }

  /**
   * Emit event to listeners
   */
  emit(event, data) {
    const callbacks = this.listeners.get(event);
    if (callbacks) {
      callbacks.forEach(callback => {
        try {
          callback(data);
        } catch (error) { console.warn('WS: listener error', error.message); }
      });
    }
  }
}

// Create singleton instance
const wsService = new WebSocketService();

export default wsService;
